import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Package } from 'lucide-react';

/**
 * Dashboard widget — Stock Alert Trends (30 days).
 * Pure-presentation extraction; UI and behaviour identical to the previous inline markup.
 */
export default function StockAlertTrendsWidget({ stockAlertTrends }) {
  const hasData = stockAlertTrends.trend?.length > 0 && stockAlertTrends.summary?.total_checks > 0;
  const maxVal = hasData ? Math.max(...stockAlertTrends.trend.map(t => t.alerts), 1) : 1;

  return (
    <Card className="border-border border-warning/30 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 dark:border-amber-700" data-testid="stock-alert-trends-widget">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center">
            <TrendingUp size={16} className="text-warning dark:text-amber-400" />
          </div>
          <span>Stock Alert Trends</span>
          <Badge className="ml-auto bg-warning/20 text-warning dark:bg-amber-900/50 dark:text-amber-300 text-[10px]">
            30 days
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-white/60 dark:bg-muted/60 rounded-lg border border-warning/30 dark:border-amber-800 text-center">
                <p className="text-[10px] text-muted-foreground">Avg/Day</p>
                <p className="text-lg font-bold text-warning">{stockAlertTrends.summary.avg_alerts_per_day}</p>
              </div>
              <div className="p-2 bg-white/60 dark:bg-muted/60 rounded-lg border border-warning/30 dark:border-amber-800 text-center">
                <p className="text-[10px] text-muted-foreground">Peak</p>
                <p className="text-lg font-bold text-error">{stockAlertTrends.summary.peak_count}</p>
              </div>
              <div className="p-2 bg-white/60 dark:bg-muted/60 rounded-lg border border-warning/30 dark:border-amber-800 text-center">
                <p className="text-[10px] text-muted-foreground">Days w/ Alerts</p>
                <p className="text-lg font-bold text-warning">{stockAlertTrends.summary.days_with_alerts}</p>
              </div>
            </div>
            <div className="flex items-end gap-[2px] h-16 px-1" data-testid="stock-trend-chart">
              {stockAlertTrends.trend.slice(-30).map((d, i) => {
                const h = d.alerts > 0 ? Math.max((d.alerts / maxVal) * 100, 8) : 2;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-muted text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      {d.date?.slice(5)}: {d.alerts}
                    </div>
                    <div
                      className={`w-full rounded-t-sm transition-all ${d.alerts > 0 ? 'bg-amber-400 hover:bg-warning' : 'bg-warning/20 dark:bg-amber-800/30'}`}
                      style={{ height: `${h}%` }}
                    />
                  </div>
                );
              })}
            </div>
            {stockAlertTrends.top_items?.length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground mb-1.5 font-medium">Frequently Low Stock Items</p>
                {stockAlertTrends.top_items.slice(0, 4).map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b border-warning/30 dark:border-amber-800 last:border-0" data-testid={`trend-item-${i}`}>
                    <span className="text-xs font-medium truncate flex-1">{item.name}</span>
                    <Badge variant="outline" className="text-[9px] border-warning/30 text-warning ml-2">{item.frequency}x</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <Package size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No stock alert history yet</p>
            <p className="text-xs mt-1">Trends will appear after stock checks run</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
